import { type InputHTMLAttributes, forwardRef } from 'react';

import { sendGTMEvent } from '@/core/components/google-tag-manger';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared/ui/icon/icon';

import { inputClasses } from './input';

interface IInputSearchProps extends InputHTMLAttributes<HTMLInputElement> {
    asForm?: boolean;
    className?: string;
    classesNames?: { inputClass: string; iconClass: string };
    searchPage: string;
    searchButtonLabel: string;
    resetButtonLabel: string;
}

export const InputSearch = forwardRef<HTMLInputElement, IInputSearchProps>(
    (
        {
            asForm = true,
            classesNames,
            className,
            searchPage,
            searchButtonLabel,
            resetButtonLabel,
            ...props
        }: IInputSearchProps,
        ref,
    ) => {
        const renderInput = () => (
            <input
                ref={ref}
                id='input'
                type='search'
                name='query'
                className={cn(
                    inputClasses,
                    'peer bg-surface-gray-lightest text-body-md-400 h-12 rounded-full border-0 pr-12 pl-8 hover:border-0 focus:border-0 focus-visible:border-0 active:border-0 [&::-webkit-search-cancel-button]:hidden',
                    classesNames?.inputClass,
                )}
                {...props}
            />
        );

        const renderSubmit = () => (
            <button
                type='submit'
                name='search'
                className={cn(
                    'absolute top-1/2 right-6 hidden size-6 -translate-y-1/2 cursor-pointer peer-placeholder-shown:block',
                    classesNames?.iconClass,
                )}
                aria-label={searchButtonLabel}>
                <Icon name='IcSearch' className='size-full' />
            </button>
        );

        const renderReset = () => (
            <button
                type='reset'
                className={cn(
                    'absolute top-1/2 right-6 size-6 -translate-y-1/2 cursor-pointer peer-placeholder-shown:hidden',
                    classesNames?.iconClass,
                )}
                aria-label={resetButtonLabel}>
                <Icon name='IcClose' className='size-full' />
            </button>
        );

        const wrapperClasses = cn('relative h-12 w-full', className);

        const trackSubmit = () => {
            sendGTMEvent({
                event: 'dlv_push_site_search',
            });
        };

        return asForm ? (
            <form
                action={searchPage}
                className={cn('relative h-12 w-full', className)}
                onReset={e => e.currentTarget.querySelector('input')?.focus()}
                onSubmit={() => {
                    trackSubmit();
                }}>
                {renderInput()}
                {renderSubmit()}
                {renderReset()}
            </form>
        ) : (
            <div className={wrapperClasses}>
                {renderInput()}
                {renderSubmit()}
                {renderReset()}
            </div>
        );
    },
);

InputSearch.displayName = 'InputSearch';
